import { useEffect, useMemo } from 'react'
import { shallowEqual } from 'react-redux'

import type { QueryResultLike } from '@krp/client-runtime'
import { aqsiRegisterLink, type AqsiRegisterLink } from '@krp/resource-aqsi-ui'

import { useAppDispatch, useAppSelector } from '../../app/hooks'
import type { RootState } from '../../app/types'

type Links = Readonly<Record<number, QueryResultLike<AqsiRegisterLink> | undefined>>

const NO_LINKS: Links = {}

/**
 * Связь двух уровней по кассам (`GET …/link`): чтение, кассе ничего не отправляет и в цикле не повторяется —
 * перечитывается по событию службы («проверили кассу») и по явному «Обновить». Подписка по одной на кассу; число
 * касс меняется со списком, поэтому подписки ведёт эффект, а не фиксированный набор хуков.
 */
export function useRegisterLinks(resourceId: string, deviceIds: readonly number[]): Links {
  const dispatch = useAppDispatch()
  const key = [...new Set(deviceIds)].sort((a, b) => a - b).join(',')
  const ids = useMemo(() => (key === '' ? [] : key.split(',').map(Number)), [key])

  useEffect(() => {
    if (resourceId === '' || ids.length === 0) return undefined
    const subscriptions = ids.map((deviceId) => dispatch(aqsiRegisterLink.initiate({ resourceId, deviceId })))
    return () => {
      for (const subscription of subscriptions) subscription.unsubscribe()
    }
  }, [dispatch, ids, resourceId])

  const selectors = useMemo(
    () => ids.map((deviceId) => [deviceId, aqsiRegisterLink.select({ resourceId, deviceId })] as const),
    [ids, resourceId],
  )
  // Результаты RTK Query стабильны, пока запрос не менялся: поверхностного сравнения достаточно.
  return useAppSelector((state: RootState) => {
    if (resourceId === '' || selectors.length === 0) return NO_LINKS
    const links: Record<number, QueryResultLike<AqsiRegisterLink>> = {}
    for (const [deviceId, select] of selectors) links[deviceId] = select(state)
    return links
  }, shallowEqual)
}
